import { Button, Checkbox, Divider, Loader } from '@busybusy/webapp-react-ui';
import { EquipmentModelType } from '__generated__/graphql';
import classNames from 'classnames';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import EmptyState from 'components/foundation/state-templates/EmptyState/EmptyState';
import { useOpenable } from 'hooks';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { highlight } from 'utils/stringUtils';
import './EquipmentAddOnDialog.scss';
import EquipmentAddOnDialogHeader from './EquipmentAddOnDialogHeader';
import useEquipmentAddOnData from './hooks/useEquipmentAddOnData';

export interface IEquipmentAddOnDialogProps {
  className?: ClassName;
  isOpen: boolean;
  selectedId?: string;
  currentSelections?: string[];
  onClose: () => void;
  onSubmit: (selectedIds: string[]) => void;
}

export interface IEquipmentAddOnPickerRow {
  id: string;
  equipmentName: string;
  make: string;
  model: string;
}

const EquipmentAddOnDialog = (props: IEquipmentAddOnDialogProps) => {
  const { className, isOpen, selectedId, currentSelections, onClose, onSubmit } = props;

  const limitReachedOpenable = useOpenable();

  const [selectedType, setSelectedType] = useState<EquipmentModelType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [data, setData] = useState<IEquipmentAddOnPickerRow[]>([]);
  const [filteredData, setFilteredData] = useState<IEquipmentAddOnPickerRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const overLimitNumber = selectedIds.length - 20;

  const { getEquipment } = useEquipmentAddOnData();

  useEffect(() => {
    setSelectedIds(currentSelections ?? []);
  }, [currentSelections]);

  useEffect(() => {
    setIsLoading(true);
    getData(selectedType, selectedCategory, selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, selectedCategory, selectedId]);

  useEffect(() => {
    const filteredData = searchValue
      ? data.filter((item) => item.equipmentName.toLowerCase().includes(searchValue.toLowerCase()))
      : data;
    setFilteredData(filteredData);
  }, [searchValue, data]);

  const getData = async (type: EquipmentModelType | null, categoryId: string | null, selectedId?: string) => {
    const equipment = await getEquipment(type, categoryId, selectedId);
    const equipmentPickerData: IEquipmentAddOnPickerRow[] = equipment.map((e) => {
      return {
        id: e.id,
        equipmentName: e.equipmentName,
        make: e.model?.make.title ?? '',
        model: e.model?.title ?? '',
      };
    });
    setData(equipmentPickerData);
    setFilteredData(equipmentPickerData);
    setIsLoading(false);
  };

  const resetAll = () => {
    setSearchValue('');
    setSelectedCategory(null);
    setSelectedIds([]);
    setSelectedType(null);
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleSubmit = () => {
    if (overLimitNumber > 0) {
      limitReachedOpenable.open();
    } else {
      onSubmit(selectedIds);
    }
  };

  const handleItemChecked = (checked: boolean, value: string) => {
    setSelectedIds((prevSelectedIds) =>
      checked ? [...prevSelectedIds, value] : prevSelectedIds.filter((id) => id !== value)
    );
  };

  const classes = classNames('equipment-add-on-dialog', className);

  const renderCheckboxRow = (item: IEquipmentAddOnPickerRow) => (
    <div className="add-on-row py-3" key={item.id}>
      <Checkbox onChange={handleItemChecked} checked={selectedIds.includes(item.id)} value={item.id} />
      <div>
        {highlight(item.equipmentName, searchValue)}
        <br />
        <span className="make-model">
          {item.make} {item.model}
        </span>
      </div>
    </div>
  );

  return (
    <HeaderDialog className={classes} title={t('Select Equipment')} isOpen={isOpen} onClose={handleClose}>
      <div>
        <EquipmentAddOnDialogHeader
          className="m-5"
          searchValue={searchValue}
          selectedCategory={selectedCategory}
          selectedType={selectedType}
          setSearchValue={setSearchValue}
          onTypeSelect={setSelectedType}
          onCategorySelect={setSelectedCategory}
        />
      </div>
      <Divider />
      <div className={'selector-container mx-5'}>
        {isLoading ? (
          <Loader isOpen={isLoading} />
        ) : (
          <>
            {filteredData.length > 0 ? (
              <>{filteredData.map(renderCheckboxRow)}</>
            ) : (
              <EmptyState title={t('No Materials Found')} />
            )}
          </>
        )}
      </div>
      <div>
        <Divider />
        <Button className={'m-5'} onClick={handleSubmit} type={'primary'} disabled={selectedIds.length < 1}>
          {t('Submit')}
        </Button>
      </div>
      <HeaderDialog
        title={t('Maximum 20 Add-Ons')}
        isOpen={limitReachedOpenable.isOpen}
        divider={false}
        onClose={limitReachedOpenable.close}
      >
        <div className={'mx-8 my-2'}>
          <div>
            {t(
              `You will need to deselect ${overLimitNumber} Add-On${overLimitNumber > 1 ? 's' : ''} in order to continue.`
            )}
          </div>
          <Button type="secondary" onClick={limitReachedOpenable.close} className={'my-5'}>
            {t('Close')}
          </Button>
        </div>
      </HeaderDialog>
    </HeaderDialog>
  );
};

export default EquipmentAddOnDialog;
